import { Signal } from '@latitude/signal';
import { Spinner } from '@latitude/spinner';
import { cn } from '@latitude/token-utils';
import { getSpinnerSize, getSpinnerVariant } from 'helpers';
import type { ComponentRef, Ref } from 'react';
import { useMemo } from 'react';
import type { ButtonProps, ButtonSize, ButtonType, ButtonVariant } from 'types';

import { ButtonAsChild } from './ButtonAsChild';
import {
  buttonVariants,
  containerVariants,
  iconsVariants,
  textAreaVariants,
} from './variants';

/**
 * A customizable button component.
 *
 * @component
 * @example
 * // Usage:
 * <Button variant="primary" size="medium">
 *   Click me
 * </Button>
 *
 * @param {Object} props - The component props.
 * @param {string} [props.className] - Additional CSS class name(s) for the button.
 * @param {string} [props.variant='primary'] - The button variant.
 * @param {string} [props.size='medium'] - The button size.
 * @param {string} [props.type='filled'] - The button type style.
 * @param {boolean} [props.disabled=false] - Whether the button is disabled.
 * @param {boolean} [props.loading=false] - Whether the button is in a loading state.
 * @param {boolean} [props.asChild=false] - Whether the button should be rendered as a child component.
 * @param {boolean} [props.selected=false] - Whether the button is selected.
 * @param {React.ReactNode} [props.leadingElement] - The leading icon element.
 * @param {React.ReactNode} [props.trailingElement] - The trailing icon element.
 * @param {React.ReactNode} [props.children] - The content of the button.
 * @param {React.HTMLProps<HTMLButtonElement>} [props...props] - Additional props to be spread to the underlying button element.
 * @param {React.Ref<ComponentRef<'button'>>} ref - The ref passed through to the underlying button element.
 * @returns {React.ReactElement} The rendered Button component.
 */
const Button = ({
  className,
  variant = 'primary',
  size = 'medium',
  type = 'filled',
  disabled = false,
  loading = false,
  asChild = false,
  selected = false,
  contentDirection = 'horizontal',
  leadingElement = undefined,
  trailingElement = undefined,
  children,
  onClick,
  signal,
  htmlType,
  rawProps = {},
  'data-testid': testID = 'btn-test',
  ref,
  ...variantProps
}: ButtonProps & {
  ref?: Ref<ComponentRef<'button'>>;
}) => {
  const buttonVariantsClasses: string = useMemo(
    () =>
      buttonVariants({
        variant,
        type,
        size,
        disabled,
        loading,
        selected,
      }),
    [variant, type, size, disabled, selected, loading],
  );

  const sharedProps = {
    ref,
    className: cn('relative', buttonVariantsClasses, className),
    'data-loading': loading,
    'data-testid': testID,
    tabIndex: loading || disabled ? -1 : 0,
    disabled: disabled || loading,
    onClick,
    ...variantProps,
    ...rawProps,
  };

  if (asChild) {
    return (
      <ButtonAsChild
        {...sharedProps}
        // A Slot target may render a non-button element, which shouldn't get
        // a `type` attribute unless the consumer explicitly asked for one.
        type={htmlType}
        loading={loading}
        leadingElement={leadingElement}
        trailingElement={trailingElement}
        size={size}
        spinnerVariant={getSpinnerVariant(variant, type)}
        signal={signal}
      >
        {children}
      </ButtonAsChild>
    );
  }

  return (
    <button
      {...sharedProps}
      // Default to type="button" so a Button inside a <form> doesn't
      // accidentally submit it (the HTML default is "submit"). Submit
      // buttons opt in with htmlType="submit".
      type={htmlType ?? 'button'}
    >
      <div className={containerVariants({ size, loading, contentDirection })}>
        {loading && (
          <Spinner
            size={getSpinnerSize(size)}
            className="pointer-events-none absolute m-auto"
            variant={getSpinnerVariant(variant, type)}
          />
        )}
        {leadingElement && (
          <span
            className={cn(
              'pointer-events-none',
              iconsVariants({ size, loading }),
            )}
          >
            {leadingElement}
          </span>
        )}
        {children && (
          <div
            className={cn(
              'pointer-events-none w-full',
              textAreaVariants({ size, loading, type }),
            )}
          >
            {children}
          </div>
        )}
        {trailingElement && (
          <span
            className={cn(
              'pointer-events-none',
              iconsVariants({ size, loading }),
            )}
          >
            {trailingElement}
          </span>
        )}
      </div>
      {signal && (
        <Signal
          {...signal}
          border
          className={cn([
            'pointer-events-none',
            'absolute',
            '-right-sizing-sys-size-4',
            '-top-sizing-sys-size-4',
          ])}
        />
      )}
    </button>
  );
};

Button.displayName = 'Button';

export {
  Button,
  buttonVariants,
  type ButtonProps,
  type ButtonSize,
  type ButtonType,
  type ButtonVariant,
};
